// src/pages/AdminUserManager.js
// ✅ SUPER ADMIN ONLY - User management restricted to super_admin role
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, doc,
  query, orderBy, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { ROLES, isSuperAdmin } from '../services/authService';
import './AdminUserManager.css'; // ✅ NEW: Import CSS
import {
  FaWrench, FaBullseye, FaCrown, FaStar, FaLock, FaLockOpen, FaEdit, FaTimes,
  FaExclamationTriangle, FaSpinner, FaSave, FaUsers, FaCheckCircle, FaSearch,
  FaPlus, FaPhone, FaMapMarkerAlt,
} from 'react-icons/fa';

const ROLE_OPTIONS = [
  { value: ROLES.TECHNICIAN, label: 'Technician', icon: <FaWrench /> },
  { value: ROLES.SUPERVISOR, label: 'Supervisor', icon: <FaBullseye /> },
  { value: ROLES.ADMIN, label: 'Admin', icon: <FaCrown /> }
  // Note: SUPER_ADMIN cannot be assigned through UI - must be set directly in Firestore
];

const emptyForm = {
  name: '', phone: '', role: ROLES.TECHNICIAN, active: true, region: '', notes: ''
};

function AdminUserManager({ user }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null); // null | 'create' | 'edit'
  const [selected, setSelected] = useState(null);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);

  // ✅ ACCESS CONTROL: Only super admin can access this page
  useEffect(() => {
    if (!isSuperAdmin(user)) {
      alert('Access Denied: Only Super Admin can manage users.');
      // In production, you'd redirect them away
      return;
    }
    loadUsers();
  }, [user]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('name')));
      // ✅ FIX: accounts waiting on approval (or denied) have role: null —
      // they belong in the Access Requests tab, not here. Without this
      // filter, this screen crashes trying to display a user with no role.
      const approvedOnly = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role);
      setUsers(approvedOnly);
    } catch (e) {
      console.error('Error loading users:', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const matchRole   = filter === 'all' || u.role === filter;
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.toLowerCase().includes(search.toLowerCase()) ||
      u.region?.toLowerCase().includes(search.toLowerCase());
    return matchRole && matchSearch;
  });

  // ── Validate form ──────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.name.trim())  return 'Name is required';
    if (!form.phone.trim()) return 'Phone is required';
    if (!form.role)         return 'Role is required';
    return '';
  };

  // ── Create user ────────────────────────────────────────────────────────────
  // ✅ REMOVED: Direct PIN-based user creation. New accounts now come
  // exclusively through the Request Access → Admin Approval flow (see the
  // "Access Requests" tab). This keeps ONE way to create an account instead
  // of two competing, inconsistent paths.

  // ── Edit user ──────────────────────────────────────────────────────────────
  const handleEdit = async () => {
    if (!isSuperAdmin(user)) {
      setFormError('Only Super Admin can edit users');
      return;
    }

    // Prevent editing super admin users (safety measure)
    if (selected?.role === ROLES.SUPER_ADMIN) {
      setFormError('Cannot edit Super Admin accounts through UI');
      return;
    }

    const err = validate();
    if (err) { setFormError(err); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', selected.id), {
        name:      form.name.trim(),
        phone:     form.phone.trim(),
        role:      form.role,
        region:    form.region.trim(),
        notes:     form.notes.trim(),
        updatedAt: Timestamp.now(),
        updatedBy: user.id
      });
      await loadUsers();
      setModal(null);
      setSelected(null);
      setForm(emptyForm);
      setFormError('');
    } catch (e) {
      setFormError('Failed to update user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active ──────────────────────────────────────────────────────────
  const handleToggleActive = async (u) => {
    if (!isSuperAdmin(user)) {
      alert('Only Super Admin can activate/deactivate users');
      setConfirmDeactivate(null);
      return;
    }

    // Prevent deactivating super admin users
    if (u.role === ROLES.SUPER_ADMIN) {
      alert('Cannot deactivate Super Admin accounts');
      setConfirmDeactivate(null);
      return;
    }

    try {
      await updateDoc(doc(db, 'users', u.id), {
        active:    !u.active,
        updatedAt: Timestamp.now(),
        updatedBy: user.id
      });
      await loadUsers();
    } catch (e) {
      alert('Failed to update user status.');
    } finally {
      setConfirmDeactivate(null);
    }
  };

  const openEdit = (u) => {
    // Prevent editing super admin accounts
    if (u.role === ROLES.SUPER_ADMIN) {
      alert('Super Admin accounts can only be modified directly in Firestore');
      return;
    }

    setSelected(u);
    setForm({ 
      name: u.name || '', 
      phone: u.phone || '', 
      role: u.role || ROLES.TECHNICIAN, 
      active: u.active, 
      region: u.region || '', 
      notes: u.notes || '' 
    });
    setFormError('');
    setModal('edit');
  };

  // ✅ CHANGED: "New User" no longer opens a create form — accounts are
  // now created by the person themselves via Request Access, then
  // approved in the Access Requests tab. This just points admins there.
  const openCreate = () => {
    alert('New accounts are created via self-registration now.\n\nAsk the person to click "Request Access" on the login screen — then approve them in the Access Requests tab, where you\'ll also assign their role.');
  };

  // Returns the CSS class for a role's color token (see role-* rules in
  // AdminUserManager.css). super_admin gets its own solid-badge treatment
  // via a modifier on .aum-role-badge, so it doesn't need a token here.
  const roleClass = (role) => {
    if (role === ROLES.ADMIN) return 'role-admin';
    if (role === ROLES.SUPERVISOR) return 'role-supervisor';
    return 'role-technician';
  };

  const stats = {
    total:       users.length,
    active:      users.filter(u => u.active).length,
    superAdmins: users.filter(u => u.role === ROLES.SUPER_ADMIN).length,
    admins:      users.filter(u => u.role === ROLES.ADMIN).length,
    supervisors: users.filter(u => u.role === ROLES.SUPERVISOR).length,
    technicians: users.filter(u => u.role === ROLES.TECHNICIAN).length
  };

  // ✅ ACCESS DENIED for non-super-admins
  if (!isSuperAdmin(user)) {
    return (
      <div className="aum-denied">
        <div className="aum-denied-icon"><FaLock /></div>
        <h2>Access Denied</h2>
        <p>
          Only Super Admin can access user management.
        </p>
      </div>
    );
  }

  // ── User creation/edit modal ───────────────────────────────────────────────
  const UserModal = modal ? (
    <div className="aum-modal-overlay" onClick={() => setModal(null)}>
      <div className="aum-modal" onClick={e => e.stopPropagation()}>
        <div className="aum-modal-header">
          <h3 className="aum-modal-title"><FaEdit /> Edit User</h3>
          <button className="aum-modal-close" onClick={() => setModal(null)}><FaTimes /></button>
        </div>
        <div className="aum-modal-body">
          {formError && <div className="aum-error-box"><FaExclamationTriangle /> {formError}</div>}
          
          <div className="aum-field">
            <label className="aum-field-label">Full Name</label>
            <input className="aum-modal-input" type="text" value={form.name} 
              onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., John Doe" />
          </div>

          <div className="aum-field">
            <label className="aum-field-label">Phone Number</label>
            <input className="aum-modal-input" type="tel" value={form.phone} 
              onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="e.g., 0712345678" />
          </div>

          <div className="aum-field">
            <label className="aum-field-label">Role</label>
            <div className="aum-role-row">
              {ROLE_OPTIONS.map(r => {
                const isSelected = form.role === r.value;
                return (
                  <button key={r.value} type="button"
                    className={`aum-role-btn ${roleClass(r.value)} ${isSelected ? 'selected' : ''}`}
                    onClick={() => setForm({ ...form, role: r.value })}>
                    {r.icon} {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="aum-field">
            <label className="aum-field-label">Region (Optional)</label>
            <input className="aum-modal-input" type="text" value={form.region} 
              onChange={e => setForm({ ...form, region: e.target.value })} placeholder="e.g., Nairobi, Mombasa" />
          </div>

          <div className="aum-field">
            <label className="aum-field-label">Notes (Optional)</label>
            <textarea className="aum-modal-input textarea" value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
          </div>
        </div>
        <div className="aum-modal-footer">
          <button className="aum-cancel-btn" onClick={() => setModal(null)}>Cancel</button>
          <button className="aum-save-btn" onClick={handleEdit} disabled={saving}>
            {saving ? <><FaSpinner className="aum-spin" /> Saving...</> : <><FaSave /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ── Deactivate confirm ─────────────────────────────────────────────────────
  const ConfirmModal = confirmDeactivate ? (
    <div className="aum-modal-overlay">
      <div className="aum-modal confirm">
        <div className="aum-confirm-body">
          <div className="aum-confirm-icon">
            {confirmDeactivate.active ? <FaLock /> : <FaLockOpen />}
          </div>
          <h3 className="aum-confirm-title">
            {confirmDeactivate.active ? 'Deactivate User?' : 'Reactivate User?'}
          </h3>
          <p className="aum-confirm-text">
            {confirmDeactivate.active
              ? `${confirmDeactivate.name} will no longer be able to log in.`
              : `${confirmDeactivate.name} will be able to log in again.`}
          </p>
          <div className="aum-confirm-actions">
            <button className="aum-cancel-btn" onClick={() => setConfirmDeactivate(null)}>Cancel</button>
            <button
              className={`aum-save-btn ${confirmDeactivate.active ? 'danger' : 'success'}`}
              onClick={() => handleToggleActive(confirmDeactivate)}>
              {confirmDeactivate.active ? <><FaLock /> Deactivate</> : <><FaLockOpen /> Reactivate</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="aum-page">
      {UserModal}
      {ConfirmModal}

      {/* Stats row */}
      <div className="aum-stats-row">
        {[
          { label: 'Total Users',   value: stats.total,       icon: <FaUsers /> },
          { label: 'Active',        value: stats.active,      icon: <FaCheckCircle /> },
          { label: 'Super Admins',  value: stats.superAdmins, icon: <FaStar /> },
          { label: 'Admins',        value: stats.admins,      icon: <FaCrown /> },
          { label: 'Supervisors',   value: stats.supervisors, icon: <FaBullseye /> },
          { label: 'Technicians',   value: stats.technicians, icon: <FaWrench /> }
        ].map(stat => (
          <div key={stat.label} className="aum-stat-card">
            <div className="aum-stat-icon">{stat.icon}</div>
            <div className="aum-stat-value">{stat.value}</div>
            <div className="aum-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="aum-toolbar">
        <div className="aum-search-wrap">
          <FaSearch className="aum-search-icon" />
          <input className="aum-search-input" placeholder="Search by name, phone, region..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="aum-filter-row">
          {['all', ROLES.TECHNICIAN, ROLES.SUPERVISOR, ROLES.ADMIN, ROLES.SUPER_ADMIN].map(f => (
            <button key={f} className={`aum-filter-pill ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}>
              {f === 'all' ? `All (${stats.total})` : 
               f === ROLES.TECHNICIAN ? <><FaWrench /> Techs ({stats.technicians})</> : 
               f === ROLES.SUPERVISOR ? <><FaBullseye /> Supervisors ({stats.supervisors})</> :
               f === ROLES.ADMIN ? <><FaCrown /> Admins ({stats.admins})</> :
               <><FaStar /> Super Admins ({stats.superAdmins})</>}
            </button>
          ))}
        </div>
        <button className="aum-create-btn" onClick={openCreate}><FaPlus /> New User</button>
      </div>

      {/* User list */}
      {loading ? (
        <div className="aum-loading">Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className="aum-empty">No users found</div>
      ) : (
        <div className="aum-user-list">
          {filteredUsers.map(u => {
            const isSuperAdminUser = u.role === ROLES.SUPER_ADMIN;
            return (
              <div key={u.id} className={`aum-user-card ${u.active ? '' : 'inactive'}`}>
                {/* Avatar */}
                <div className="aum-avatar">{u.name?.charAt(0).toUpperCase() || '?'}</div>

                {/* Info */}
                <div className="aum-user-info">
                  <div className="aum-user-name">
                    {u.name}
                    {isSuperAdminUser && <span className="aum-super-star"><FaStar /></span>}
                  </div>
                  <div className="aum-user-meta">
                    <span><FaPhone /> {u.phone}</span>
                    {u.region && <span><FaMapMarkerAlt /> {u.region}</span>}
                    <span className={`aum-role-badge ${isSuperAdminUser ? 'role-super_admin' : roleClass(u.role)}`}>
                      {u.role === ROLES.SUPER_ADMIN ? <FaStar /> : 
                       u.role === ROLES.ADMIN ? <FaCrown /> : 
                       u.role === ROLES.SUPERVISOR ? <FaBullseye /> : <FaWrench />} {(u.role || 'unknown').replace('_', ' ')}
                    </span>
                    <span className={`aum-status-badge ${u.active ? 'active' : 'inactive'}`}>
                      {u.active ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="aum-user-actions">
                  <button 
                    className="aum-edit-btn"
                    onClick={() => openEdit(u)}
                    disabled={isSuperAdminUser}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    className={`aum-toggle-btn ${u.active ? 'active' : 'inactive'}`}
                    onClick={() => setConfirmDeactivate(u)}
                    disabled={isSuperAdminUser}
                  >
                    {u.active ? <><FaLock /> Deactivate</> : <><FaLockOpen /> Activate</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminUserManager;
